import { useMemo } from 'react';
import Select from 'react-select';
import useThemeState from '@/states/theme.state';
import { Region, RegionOption, buildRegionOptions } from './region-options';

interface RegionSelectProps {
  value: string;
  regions: Region[];
  placeholder: string;
  notFoundMessage: string;
  disabled?: boolean;
  hasError?: boolean;
  inputId?: string;
  onChange: (regionId: string) => void;
}

/** Dropdown wilayah (Provinsi, Kota/Kabupaten) yang bisa dicari dengan mengetik (cocok substring, tanpa peduli kapital). */
export const RegionSelect = ({
  value,
  regions,
  placeholder,
  notFoundMessage,
  disabled,
  hasError,
  inputId,
  onChange,
}: Readonly<RegionSelectProps>) => {
  const isDark = useThemeState(s => s.mode === 'dark');
  const options = useMemo(() => buildRegionOptions(regions), [regions]);
  const selected = options.find(option => option.value === value) ?? null;

  const textColor = isDark ? '#f3f4f6' : '#111827';
  const surface = isDark ? '#1f2937' : '#fff';
  const disabledSurface = isDark ? '#374151' : '#f3f4f6';
  const normalBorder = isDark ? '#4b5563' : '#d1d5db';
  const borderColor = hasError ? '#ef4444' : normalBorder;
  const focusedOptionBg = isDark ? '#374151' : '#eff6ff';

  return (
    <Select<RegionOption>
      inputId={inputId}
      options={options}
      value={selected}
      isDisabled={disabled}
      placeholder={placeholder}
      noOptionsMessage={() => notFoundMessage}
      onChange={option => onChange(option?.value ?? '')}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      closeMenuOnScroll
      styles={{
        menuPortal: base => ({ ...base, zIndex: 9999 }),
        control: (base, state) => ({
          ...base,
          minHeight: 38,
          borderRadius: 12,
          backgroundColor: state.isDisabled ? disabledSurface : surface,
          borderColor,
          '&:hover': { borderColor },
          boxShadow: state.isFocused ? '0 0 0 2px #3b82f6' : 'none',
          cursor: state.isDisabled ? 'not-allowed' : 'default',
        }),
        singleValue: base => ({ ...base, color: textColor }),
        input: base => ({ ...base, color: textColor }),
        menu: base => ({ ...base, backgroundColor: surface }),
        option: (base, state) => ({
          ...base,
          cursor: 'pointer',
          color: textColor,
          backgroundColor: state.isFocused ? focusedOptionBg : 'transparent',
        }),
      }}
    />
  );
};
